import { useState, useEffect, useRef, useMemo } from 'react';
import { useGameState } from '../../../net/GameProvider.js';
import { useI18n } from '../../../i18n.js';
import { getUserAvatar } from '../../../utils/avatar.js';
import { Icon } from '../../Icon.js';
import { ReactionBubble, ReactionPicker } from '../../ReactionUI.js';
import { Opponents } from '../Opponents.js';
import { MyHand } from '../MyHand.js';
import { Big2Table } from './Big2Table.js';
import { Big2Controls } from './Big2Controls.js';
import { PauseOverlay } from '../common/PauseOverlay.js';
import { TurnIndicator } from '../common/TurnIndicator.js';
import { formatCardDisplay } from '../../CardUI.js';
import { playCardSelectSound, playYourTurnSound, playTimerRedTick } from '../../../sound.js';
import { classify, beats, allCombos, sortBySuit, sortByRank, sortByPairs } from '@big2/shared';
import { trackEvent } from '../../../analytics.js';
import { TableInteractionsProvider } from '../TableInteractions.js';

export function Big2GameScreen({ onOpenProfile }: { onOpenProfile?: () => void }) {
  const { state, actions } = useGameState();
  const { lang, t } = useI18n();
  const isMn = lang === 'mn';
  const { game, reactions } = state;

  const [selectedCards, setSelectedCards] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<'rank' | 'suit' | 'pairs'>('rank');
  const [sortVersion, setSortVersion] = useState<number>(0);
  const prevTurnPlayerRef = useRef<string | null>(null);
  const lastRedTickSecRef = useRef<number | null>(null);

  // Clear selection if hand changes or when paused
  useEffect(() => {
    setSelectedCards(new Set());
  }, [game?.myHand.length, game?.pause?.status]);

  const isMyTurn = !!game && game.activePlayerId === game.myPlayerId;
  const pause = game?.pause ?? null;
  const isPaused = pause?.status === 'paused';
  const isRequestingPause = pause?.status === 'requesting';

  // Play chime when it becomes our turn
  useEffect(() => {
    if (!game || isPaused) return;
    if (game.activePlayerId !== prevTurnPlayerRef.current) {
      prevTurnPlayerRef.current = game.activePlayerId;
      lastRedTickSecRef.current = null;
      if (game.activePlayerId === game.myPlayerId) {
        playYourTurnSound();
      }
    }
  }, [game?.activePlayerId, game?.myPlayerId, isPaused]);

  // Timer rendering + red timer warning sound
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  useEffect(() => {
    if (!game?.timeoutEndsAt || isPaused) {
      setTimeLeft(null);
      lastRedTickSecRef.current = null;
      return;
    }
    const tick = () => {
      const serverNow = Date.now() + (state.serverOffsetMs ?? 0);
      const remain = Math.max(0, game.timeoutEndsAt! - serverNow);
      setTimeLeft(remain);
      if (game.activePlayerId === game.myPlayerId && remain > 0 && remain <= 5000) {
        const sec = Math.ceil(remain / 1000);
        if (lastRedTickSecRef.current !== sec) {
          lastRedTickSecRef.current = sec;
          playTimerRedTick(sec);
        }
      }
    };
    tick();
    const iv = setInterval(tick, 100);
    return () => clearInterval(iv);
  }, [game?.timeoutEndsAt, game?.activePlayerId, game?.myPlayerId, isPaused, state.serverOffsetMs]);

  if (!game) return null;

  const mustPlay = isMyTurn && !game.trick; // Trick leader cannot pass in Big Two

  const toggleCard = (card: string) => {
    if (isPaused) return;
    const next = new Set(selectedCards);
    if (next.has(card)) {
      next.delete(card);
      playCardSelectSound(false);
    } else if (next.size < 5) {
      next.add(card);
      playCardSelectSound(true);
    }
    setSelectedCards(next);
  };

  const handleSelectMultiple = (cards: string[], mode: 'add' | 'remove' | 'toggle' | 'replace' = 'toggle') => {
    if (isPaused) return;
    const next = new Set(selectedCards);
    if (mode === 'replace') {
      const limited = cards.slice(0, 5);
      setSelectedCards(new Set(limited));
      playCardSelectSound(limited.length > 0);
      return;
    }
    for (const card of cards) {
      if (mode === 'add') {
        if (next.size < 5) next.add(card);
      } else if (mode === 'remove') {
        next.delete(card);
      } else if (mode === 'toggle') {
        if (next.has(card)) next.delete(card);
        else if (next.size < 5) next.add(card);
      }
    }
    playCardSelectSound(next.size > selectedCards.size);
    setSelectedCards(next);
  };

  const selectedArray = Array.from(selectedCards);
  const currentCombo = selectedArray.length > 0 ? classify(selectedArray) : null;

  let playError: string | null = null;
  if (isMyTurn) {
    if (selectedArray.length === 0) {
      playError = t.selectCardsToPlay;
    } else if (!currentCombo) {
      playError = t.invalidCombination;
    } else if (game.mustInclude && !selectedArray.includes(game.mustInclude)) {
      playError = t.mustIncludeCard(formatCardDisplay(game.mustInclude));
    } else if (game.trick && currentCombo.cards.length !== game.trick.cards.length) {
      playError = t.mustPlayCount(game.trick.cards.length);
    } else if (game.trick && !beats(currentCombo, game.trick ? classify(game.trick.cards) : null)) {
      playError = t.doesNotBeatTrick;
    }
  }

  const handlePlay = () => {
    if (isMyTurn && !playError && !isPaused) {
      actions.playCards(selectedArray);
    }
  };

  const handlePass = () => {
    if (isMyTurn && !mustPlay && !isPaused) {
      actions.passTurn();
    }
  };

  const handleSort = (mode: 'rank' | 'suit' | 'pairs') => {
    setSortBy(mode);
    setSortVersion((v) => v + 1);
    trackEvent('hand_sort_changed', { sort_by: mode, game_type: 'big2' });
  };

  const sortedHand =
    sortBy === 'rank'
      ? sortByRank(game.myHand)
      : sortBy === 'suit'
      ? sortBySuit(game.myHand)
      : sortByPairs(game.myHand);

  const activePlayerName = game.players.find((p) => p.playerId === game.activePlayerId)?.name ?? '';
  const myReaction = reactions[game.myPlayerId];
  const myPlayerObj = game.players.find((p) => p.playerId === game.myPlayerId);
  const myPlayerName = myPlayerObj?.name ?? '';
  const myAvatar = getUserAvatar(state.profile?.id || game.myPlayerId, myPlayerName);

  const cannotBeat = useMemo(() => {
    if (!isMyTurn || !game?.trick || isPaused) return false;
    const trickCombo = classify(game.trick.cards);
    if (!trickCombo) return false;
    const combos = allCombos(game.myHand);
    const beating = combos.filter((c) => c.cards.length === trickCombo.cards.length && beats(c, trickCombo));
    return beating.length === 0;
  }, [isMyTurn, game?.trick, game?.myHand, isPaused]);

  const isFinished = game.phase === 'finished';
  const isHost = Boolean(state.lobby && state.lobby.hostId === state.playerId);
  const matchOver = Boolean(state.lastGameOver?.matchOver);

  const hasTopOpponent = game.players.length === 2 || game.players.length === 4;

  return (
    <TableInteractionsProvider onOpenProfile={onOpenProfile}>
      <div className={`game-screen${isMyTurn ? ' my-turn' : ''}${cannotBeat ? ' cannot-beat' : ''}`}>
        {/* Table Area */}
        <div className={`game-table-area${hasTopOpponent ? ' has-top-opponent' : ''}`}>
          <Opponents />
        <PauseOverlay />
        <Big2Table isPaused={isPaused} />

        {/* Local Player Floating Reaction Bubble */}
        {myReaction && (
          <div className="my-reaction-slot">
            <ReactionBubble
              reactionId={myReaction.reactionId}
              seq={myReaction.seq}
              playerName={myPlayerName}
            />
          </div>
        )}

        <TurnIndicator
          isMyTurn={isMyTurn}
          timeLeft={timeLeft}
          cannotBeat={cannotBeat}
        />
      </div>

      {/* Player Area */}
      <div className="player-area">
        <div className="sr-only">
          <span role="status">
            {isPaused
              ? isMn ? 'Тоглоом түр зогссон' : 'Game paused'
              : isMyTurn
              ? t.yourTurn
              : t.waitingForTurn(activePlayerName)}
          </span>
        </div>

        {/* Big Two Controls */}
        <Big2Controls
          isMyTurn={isMyTurn}
          mustPlay={mustPlay}
          isPaused={isPaused}
          cannotBeat={cannotBeat}
          playError={playError}
          onPass={handlePass}
          onPlay={handlePlay}
          isFinished={isFinished}
          isHost={isHost}
          matchOver={matchOver}
          onPlayAgain={() => actions.startGame({ restartMatch: false })}
          onRestartMatch={() => actions.startGame({ restartMatch: true })}
          onReturnToLobby={() => {
            if (isHost) actions.returnToLobby();
            actions.clearGameOver();
          }}
        />

        {/* Above cards: Main player's centered status bar */}
        <div className="hand-header-bar">
          <div
            className={`self-counter${state.profile?.tier === 'paid' ? ' is-vip' : ''}`}
            data-seat={game?.mySeat}
            title={isMyTurn ? t.yourTurn : myPlayerName}
          >
            <button
              className="icon-button pause-action-button"
              disabled={isPaused || (isRequestingPause && !!pause?.pauseAgreedPlayerIds.includes(game.myPlayerId))}
              onClick={() => actions.pauseAction(isRequestingPause ? 'agree_pause' : 'request')}
              aria-label={isMn ? 'Тоглоом түр зогсоох' : 'Pause game'}
              title={isMn ? 'Тоглоом түр зогсоох' : 'Pause game'}
              data-tooltip={isMn ? 'Тоглоом түр зогсоох' : 'Pause game'}
            >
              <Icon name="pause" size={14} />
            </button>
            <span
              className="self-player-avatar"
              style={{
                background: myAvatar.gradient,
                borderColor: myAvatar.borderColor,
                boxShadow: `0 0 8px ${myAvatar.glow}`,
              }}
              title={myPlayerName}
              aria-hidden="true"
            >
              {myAvatar.initial}
            </span>
            <span className="self-player-name">{myPlayerName}</span>
            <span
              className="self-card-ratio"
              title={`${game.myHand.length} / ${game.format.cardsEach} ${isMn ? 'хөзөр' : 'cards'}`}
            >
              <Icon name="cards" size={13} />
              <strong>{game.myHand.length}</strong>
              <span className="card-ratio-total">/{game.format.cardsEach}</span>
            </span>
            {game.pointLimit ? (() => {
              const myTotal = game.totals?.[game.myPlayerId] ?? 0;
              const isDanger = myTotal >= game.pointLimit * 0.8;
              const isWarning = myTotal >= game.pointLimit * 0.6;
              return (
                <span
                  className={`self-point-limit${isDanger ? ' is-danger' : isWarning ? ' is-warning' : ''}`}
                  title={`${isMn ? 'Таны оноо / Хязгаар' : 'Your Points / Limit'}: ${myTotal} / ${game.pointLimit}`}
                  aria-label={`${isMn ? 'Таны оноо / Хязгаар' : 'Your Points / Limit'}: ${myTotal} / ${game.pointLimit}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '2px 8px',
                    borderRadius: 999,
                    background: isDanger ? 'rgba(239, 68, 68, 0.28)' : 'rgba(255, 255, 255, 0.12)',
                    border: `1px solid ${isDanger ? 'rgba(239, 68, 68, 0.55)' : 'rgba(255, 255, 255, 0.22)'}`,
                    fontSize: '0.82rem',
                    fontWeight: 800,
                    color: isDanger ? '#fca5a5' : isWarning ? '#fde047' : '#f1f5f9',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Icon name="star" size={12} className="self-point-icon" />
                  <span>{myTotal}/{game.pointLimit}</span>
                </span>
              );
            })() : null}
            <ReactionPicker onSelect={actions.sendReaction} onOpenProfile={onOpenProfile} direction="up" compact />
          </div>
        </div>

        {/* Hand with Ordering Controls and Deselect Button on the right side */}
        <MyHand
          isPaused={isPaused}
          isMyTurn={isMyTurn}
          sortedHand={sortedHand}
          selectedCards={selectedCards}
          toggleCard={toggleCard}
          onSelectMultiple={handleSelectMultiple}
          sortBy={sortBy}
          sortVersion={sortVersion}
          arrangeControls={
            <div className="hand-arrange-controls">
              <div className="sort-segment" role="group" aria-label={t.sortLabel}>
                <button
                  type="button"
                  className={`sort-btn${sortBy === 'rank' ? ' is-active' : ''}`}
                  aria-pressed={sortBy === 'rank'}
                  onClick={() => handleSort('rank')}
                  title={t.sortByRank}
                >
                  {isMn ? 'Тоо' : 'Rank'}
                </button>
                <button
                  type="button"
                  className={`sort-btn${sortBy === 'suit' ? ' is-active' : ''}`}
                  aria-pressed={sortBy === 'suit'}
                  onClick={() => handleSort('suit')}
                  title={t.sortBySuit}
                >
                  {isMn ? 'Өнгө' : 'Suit'}
                </button>
                <button
                  type="button"
                  className={`sort-btn${sortBy === 'pairs' ? ' is-active' : ''}`}
                  aria-pressed={sortBy === 'pairs'}
                  onClick={() => handleSort('pairs')}
                  title={t.sortByPairs}
                >
                  {isMn ? 'Хос' : 'Pairs'}
                </button>
              </div>

              {selectedCards.size > 0 && !isPaused ? (
                <button
                  type="button"
                  className="icon-button clear-selection-button"
                  onClick={() => {
                    trackEvent('hand_selection_cleared', {
                      cleared_count: selectedCards.size,
                      game_type: 'big2',
                    });
                    setSelectedCards(new Set());
                  }}
                  aria-label={isMn ? 'Сонголтыг арилгах' : 'Clear selection'}
                  title={isMn ? 'Сонголтыг арилгах' : 'Clear selection'}
                  data-tooltip={isMn ? 'Сонголтыг арилгах' : 'Clear selection'}
                >
                  <Icon name="x" size={18} />
                </button>
              ) : (
                <div className="clear-selection-slot" aria-hidden="true" />
              )}
            </div>
          }
        />
      </div>
    </div>
    </TableInteractionsProvider>
  );
}
